<template>
    <!-- Nothing pending, nothing shown: a panel that says "all clear" every day
         is a panel people stop reading on the day it does not. -->
    <section v-if="rows.length" class="attention-list">
        <h4 class="attention-list-title">
            <b-icon :icon="icon" size="is-small" />
            <span>{{ title || $t('Needs attention') }}</span>
        </h4>

        <div v-for="row in rows" :key="row.key" class="attention-list-row">
            <b-icon :icon="row.icon" size="is-small" class="attention-list-icon" />
            <div class="attention-list-text">
                <span class="attention-list-label">{{ row.label }}</span>
                <span v-if="row.sub" class="attention-list-sub">{{ row.sub }}</span>
            </div>
            <router-link v-if="row.to" :to="row.to" class="attention-list-action">{{ row.action }}</router-link>
            <a v-else-if="row.handler" class="attention-list-action" @click="row.handler">{{ row.action }}</a>
        </div>
    </section>
</template>

<script setup>
/**
 * A short list of things somebody still has to do, each carrying the step that
 * clears it — a list of actions, not of numbers.
 *
 * Presentation only: whoever shows it builds the rows, because only they know
 * what is pending and where it is settled. The work order's own panel and the
 * timesheet's "ready to bill" banner share this so a person who learns the
 * shape on one record reads it without thinking on the other.
 *
 * @typedef {{
 *   key: string,            unique per row
 *   icon: string,           mdi name
 *   label: string,          what is pending
 *   sub?: string,           the detail under it
 *   action: string,         the caption of the way out
 *   to?: object,            a route location — renders a real link
 *   handler?: Function      or a click, when the step is a modal
 * }} AttentionRow
 */
defineProps({
    /** @type {AttentionRow[]} */
    rows: { type: Array, default: () => [] },
    title: { type: String, default: null },
    icon: { type: String, default: 'alert-circle-outline' }
});
</script>

<style scoped>
/* Marked by a rule on its opening edge rather than a filled box: it has to be
   noticed above what it sits on without turning the whole page into an alert. */
.attention-list {
    border-left: 3px solid var(--bulma-warning);
    border-radius: var(--bulma-radius);
    background: var(--bulma-scheme-main-bis);
    padding: 0.6rem 0.9rem;
    margin-bottom: 1.25rem;
}
.attention-list-title {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--bulma-warning-bold);
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0 0 0.35rem 0;
}
.attention-list-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.3rem 0;
}
.attention-list-row + .attention-list-row {
    border-top: 1px solid var(--bulma-border-weak);
}
.attention-list-icon {
    color: var(--bulma-text-weak);
    flex-shrink: 0;
}
.attention-list-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    line-height: 1.3;
}
.attention-list-label {
    font-weight: 600;
}
.attention-list-sub {
    color: var(--bulma-text-weak);
    font-size: 0.75rem;
}
.attention-list-action {
    flex-shrink: 0;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
}
</style>
